import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import Icon from '../ui/Icon';
import { CourierBadge, StatusPill, PageSkeleton } from '../ui/primitives';
import { courierOfOrder, isUnidentifiedOrder, isRelevantOrder, formatDeliverySlot, orderAttributes, fmtMoney, CourierKey, COURIERS } from '../ui/couriers';
import { normalizePlace } from './europeGeo';
import { useAdminOrders, useUcsOrder, AdminOrder, UcsAddress, UcsIdentify, usePackedToday, PackedTodayOrder,} from './hooks';
import { useAdminToast } from './AdminShell';
import { API_URL } from '../config/api';
import { OrderStockTag, useStockConfig, useStockOverview } from '../magazyn/api';
import { StockTagPill } from './magazyn/shared';

// Klucz dopasowania zamówienia do znacznika Magazynu: liczbowe id Shopify (także z gid://…/Order/123).
function stockIdKey(id: unknown): string {
  return String(id ?? '').replace(/^.*\//, '').trim();
}
function stockNameKey(name?: string): string {
  return (name || '').replace(/^#/, '').trim().toLowerCase();
}

function normStatus(s?: string): 'pending' | 'inprogress' | 'packed' | 'error' {
  if (s === 'completed' || s === 'packed') return 'packed';
  if (s === 'in_progress' || s === 'packing') return 'inprogress';
  if (s === 'error') return 'error';
  return 'pending';
}

// Czytelne komunikaty błędów UCS (kod -> tekst PL). 422 z /identify to zwykle stan
// biznesowy zamówienia (jeszcze nie do wysyłki), nie awaria aplikacji.
const UCS_ERR_PL: Record<string, string> = {
  rate_not_identifiable: 'Nie udało się dopasować usługi kuriera do tego zamówienia (sprawdź metodę dostawy i adres).',
  shipping_address_missing: 'Zamówienie nie ma adresu wysyłki.',
  plugin_unavailable: 'Integracja kuriera jest chwilowo niedostępna.',
  parcels_unknown: 'Nie udało się ustalić parametrów paczki dla tego zamówienia.',
  order_not_found: 'Nie znaleziono zamówienia w UCS.',
  ucs_not_configured: 'Integracja UCS nie jest skonfigurowana (Ustawienia → UCS).',
};

// „Miękkie" kody (stan zamówienia, nie błąd techniczny) — prezentowane jako informacja.
const UCS_SOFT_CODES = new Set(['rate_not_identifiable', 'shipping_address_missing', 'parcels_unknown']);

// apiClient normalizuje {error:{code,message}} -> data.error (string) + data.errorCode (kod).
function ucsErrorInfo(err: any): { code?: string; message: string; soft: boolean } {
  const code: string | undefined = err?.response?.data?.errorCode;
  const apiMsg: string | undefined =
    typeof err?.response?.data?.error === 'string' ? err.response.data.error : undefined;
  const message = (code && UCS_ERR_PL[code]) || apiMsg || err?.message || 'błąd połączenia z UCS';
  return { code, message, soft: !!code && UCS_SOFT_CODES.has(code) };
}

// pluginId UCS (np. "inpost", "dhl", "goodspeed") -> klucz odznaki kuriera.
function courierKeyOfPlugin(pluginId?: string): CourierKey | undefined {
  const p = (pluginId || '').toLowerCase();
  if (p.includes('inpost') || p.includes('paczkomat')) return 'inpost';
  if (p.includes('dhl')) return 'dhl';
  if (p.includes('goodspeed')) return 'goodspeed';
  return undefined;
}

// Czytelne nazwy dokumentów do druku (klucz UCS/PackFlow -> etykieta PL przycisku).
const DOC_LABELS: Record<string, string> = {
  packing_list: 'List pakowania',
  product_labels: 'Etykiety produktów',
};

// Etykieta kuriera dla paczki/dokumentu (np. "inpost-shipx" -> "InPost"); raw jako fallback.
function courierLabelOf(carrier?: string): string {
  const key = courierKeyOfPlugin(carrier);
  return key ? COURIERS[key].label : (carrier || 'Kurier');
}

// Waga w gramach -> czytelne kg (UCS total_weight / line_item.grams są w gramach).
function fmtGrams(g?: number | null): string | null {
  if (g == null || isNaN(g)) return null;
  if (g >= 1000) return `${(g / 1000).toFixed(g % 1000 === 0 ? 0 : 2)} kg`;
  return `${g} g`;
}

// Wartość zamówienia z pozycji UCS (Shopify nie zawsze podaje total w identify).
function totalFromLineItems(id?: UcsIdentify['order']): number | null {
  const items = id?.line_items;
  if (!items?.length) return null;
  const sum = items.reduce((s, i) => s + (parseFloat(i.price || '0') || 0) * (i.quantity || 0), 0);
  return sum > 0 ? sum : null;
}

// Wiersze adresu (UCS snake_case) do wyświetlenia, pomijając puste pola.
function addressLines(a?: UcsAddress | null): { name?: string; lines: string[]; phone?: string } | null {
  if (!a) return null;
  const name = a.name || [a.first_name, a.last_name].filter(Boolean).join(' ') || undefined;
  const lines = [
    a.company || undefined,
    a.address1 || undefined,
    a.address2 || undefined,
    [a.zip, a.city].filter(Boolean).join(' ') || undefined,
    [a.province, a.country_code].filter(Boolean).join(', ') || undefined,
  ].filter((x): x is string => !!x);
  if (!name && !lines.length && !a.phone) return null;
  return { name, lines, phone: a.phone || undefined };
}

function AddressCard({ title, addr }: { title: string; addr?: UcsAddress | null }) {
  const d = addressLines(addr);
  if (!d) return null;
  return (
    <div className="card card-pad" style={{ padding: 14, flex: 1, minWidth: 180 }}>
      <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>{title}</div>
      {d.name && <div style={{ fontWeight: 600, marginBottom: 4 }}>{d.name}</div>}
      {d.lines.map((l, i) => <div key={i} style={{ fontSize: 13, color: 'var(--fg-2)' }}>{l}</div>)}
      {d.phone && <div className="mono" style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 6 }}>{d.phone}</div>}
    </div>
  );
}

function SectionHead({ children }: { children: React.ReactNode }) {
  return <div className="card-head" style={{ padding: '0 0 10px', border: 0 }}><h3>{children}</h3></div>;
}

/**
 * Statusy pakowania dla całej listy JEDNYM zapytaniem. Wcześniej leciało N wywołań
 * `/order/:id/status`, a każdy błąd był łykany jako „not_started" — więc awaria backendu
 * wyglądała jak „nic nie spakowano". Teraz błąd propaguje się do react-query (widać go
 * w stanie zapytania), a starsze API bez trasy wsadowej obsługuje fallback poniżej.
 */
function useOrderStatuses(orders: AdminOrder[]) {
  const ids = orders.map((o) => o.id).join(',');
  return useQuery<Record<number, string>>({
    queryKey: ['orders-status', ids],
    enabled: orders.length > 0,
    queryFn: async () => {
      const list = orders.map((o) => String(o.id));
      try {
        const r = await axios.post(`${API_URL}/api/packing/orders/statuses`, { ids: list });
        const byId = (r.data || {}) as Record<string, string>;
        return Object.fromEntries(orders.map((o) => [o.id, byId[String(o.id)] || 'not_started']));
      } catch (err: any) {
        // Starsze API nie ma trasy wsadowej — dopiero wtedy pytamy pojedynczo.
        if (err?.response?.status !== 404) throw err;
        const entries = await Promise.all(orders.map(async (o) => {
          const r = await axios.get(`${API_URL}/api/packing/order/${o.id}/status`);
          return [o.id, r.data.status as string] as const;
        }));
        return Object.fromEntries(entries);
      }
    },
    refetchInterval: 30000,
  });
}

/** Spakowane dziś → kształt wiersza listy. Adresu i klienta nie ma w kartotece pakowania. */
function packedToAdminOrder(p: PackedTodayOrder): AdminOrder & { packingStatus: 'packed' } {
  return {
    id: p.id as number,
    name: p.name,
    created_at: p.packed_at,
    line_items: p.total_items ? [{ quantity: p.total_items }] : [],
    packingStatus: 'packed' as const,
    trackingNumber: p.tracking_number || undefined,
    trackingCompany: p.carrier || undefined,
  } as AdminOrder & { packingStatus: 'packed' };
}

export function OrderDrawer({ order, status, onClose, stockTag }: { order: AdminOrder; status: string; onClose: () => void; stockTag?: OrderStockTag | null }) {
  const push = useAdminToast();
  const { data: history } = useQuery<any>({
    queryKey: ['order-history', order.id],
    queryFn: async () => {
      // 404 = zamówienia nie pakowano w PackFlow (brak sesji). Inny błąd niech leci do
      // react-query, żeby „brak paczek" nie maskowało awarii odczytu.
      try { return (await axios.get(`${API_URL}/api/packing/order/${order.id}/history`)).data; }
      catch (err: any) {
        if (err?.response?.status === 404) return null;
        throw err;
      }
    },
  });

  // Pełne dane zamówienia z UCS (kurier + pozycje + adresy + fulfillment) — czysty odczyt
  // /v1/orders/:ref, który nie wywala się na 422 dla zamówień „nie do wysyłki".
  const { data: ucs, isLoading: ucsLoading, error: ucsError } = useUcsOrder(order);
  const ucsInfo = ucsError ? ucsErrorInfo(ucsError) : null;

  const ucsOrder = ucs?.order;
  const lineItems = ucsOrder?.line_items || [];
  const shippingLines = ucsOrder?.shipping_lines || [];
  const ident = ucs?.identified;
  const ful = ucs?.fulfillment;
  const identCourier = courierKeyOfPlugin(ident?.pluginId);

  // Nierozpoznana metoda wysyłki — UCS nie wystawi etykiety, więc nie udajemy przewoźnika
  // i nie oferujemy druku etykiety kurierskiej.
  const unidentified = isUnidentifiedOrder(order);
  const courier = unidentified ? 'unknown' : identCourier || courierOfOrder(order);
  const pkgs: any[] = history?.packages || [];
  const attrs = orderAttributes(order);
  const carriers = Array.from(new Set(pkgs.map((p) => p.carrier).filter(Boolean))) as string[];

  // Wartość: total_price z zamówienia -> suma z pozycji UCS (gdy brak total).
  const currency = ucsOrder?.currency || order.currency;
  const totalValue = order.total_price != null && order.total_price !== ''
    ? fmtMoney(order.total_price, currency)
    : (() => { const t = totalFromLineItems(ucsOrder); return t != null ? fmtMoney(t, currency) : '—'; })();
  const totalItems = lineItems.length
    ? lineItems.reduce((s, i) => s + (i.quantity || 0), 0)
    : (order.line_items?.reduce((s, i) => s + i.quantity, 0) ?? null);
  const customerName = addressLines(ucsOrder?.shipping_address)?.name
    || [order.customer?.first_name, order.customer?.last_name].filter(Boolean).join(' ') || '—';
  const city = ucsOrder?.shipping_address?.city || order.shipping_address?.city || '—';

  async function printDoc(kind: 'packing_list' | 'product_labels') {
    try {
      const url = kind === 'packing_list' ? '/api/printing/print-packing-list' : '/api/printing/print-product-labels';
      await axios.post(`${API_URL}${url}`, { orderId: order.id });
      push(`Wysłano do druku: ${DOC_LABELS[kind] || kind}`);
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd druku', 'err'); }
  }
  async function printShipping(carrier: string) {
    try {
      await axios.post(`${API_URL}/api/printing/print-shipping-labels`, { orderId: order.id, carrier });
      push(`Wysłano do druku: etykieta ${courierLabelOf(carrier)}`);
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd druku', 'err'); }
  }
  // Druk etykiety kurierskiej dla pojedynczej paczki spakowanego zamówienia.
  async function printPackageLabel(pkg: any, index: number) {
    const carrier = pkg?.carrier || carriers[0];
    try {
      await axios.post(`${API_URL}/api/printing/print-shipping-labels`, { orderId: order.id, carrier, packageIndex: index });
      push(`Wysłano do druku: etykieta paczki ${index + 1}`);
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd druku', 'err'); }
  }
  async function printAll() {
    await printDoc('packing_list');
    await printDoc('product_labels');
    const carrierArr = Array.from(carriers);
    for (const c of carrierArr) await printShipping(c);
    push('Drukowanie kompletu dokumentów');
  }

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer">
        <div className="drawer-head">
          <button className="btn btn-icon btn-ghost" onClick={onClose}><Icon name="x" size={18} /></button>
          <h2>{order.name}</h2>
          <StatusPill status={normStatus(status)} />
          {stockTag && <StockTagPill tag={stockTag} />}
          <div className="right"><CourierBadge courier={courier} /></div>
        </div>
        <div className="drawer-body">
          {unidentified && (
            <div
              className="pill pill-warn"
              style={{ height: 'auto', padding: '12px 14px', borderRadius: 10, marginBottom: 16, width: '100%', display: 'flex', gap: 10, alignItems: 'flex-start' }}
            >
              <Icon name="alert-circle" size={16} />
              <span>
                <b>Nierozpoznana metoda dostawy.</b> UCS nie wystawi etykiety dla tego
                zamówienia, więc nie da się go spakować ani nadać. Zgłoś numer{' '}
                <b>{order.name}</b> administratorowi systemu — konwersję wykonuje się w
                zakładce Parser w panelu UCS.
              </span>
            </div>
          )}
          <div className="card card-pad" style={{ marginBottom: 16 }}>
            <dl className="dl-grid">
              <dt>Klient</dt><dd>{customerName}</dd>
              {ucsOrder?.email && (<><dt>E-mail</dt><dd className="mono" style={{ fontSize: 13 }}>{ucsOrder.email}</dd></>)}
              <dt>Miasto</dt><dd>{city}</dd>
              <dt>Wartość</dt><dd className="num">{totalValue}</dd>
              <dt>Data</dt><dd className="mono">{new Date(order.created_at).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</dd>
              <dt>Data dostawy</dt><dd className="mono">{formatDeliverySlot(order)}</dd>
              <dt>Operator</dt><dd>{history?.operatorName || history?.operatorId || '—'}</dd>
              <dt>Pozycje</dt><dd className="num">{totalItems ?? '—'}</dd>
              {fmtGrams(ucsOrder?.total_weight) && (<><dt>Waga</dt><dd className="mono">{fmtGrams(ucsOrder?.total_weight)}</dd></>)}
              <dt>Spakowano</dt><dd className="mono">{history?.completedAt ? new Date(history.completedAt).toLocaleString('pl-PL') : '—'}</dd>
            </dl>
          </div>

          {/* Stan pobierania danych z UCS (shipments/identify). */}
          {ucsLoading && (
            <div className="muted" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 16 }}>
              <Icon name="loader" size={14} /> Ładowanie danych z UCS…
            </div>
          )}
          {ucsInfo && !ucsLoading && (
            <div className="card card-pad" style={{ padding: 12, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, color: ucsInfo.soft ? 'var(--fg-2)' : 'var(--warning-text)', background: ucsInfo.soft ? 'var(--surface-2)' : 'var(--warning-soft)' }}>
              <Icon name={ucsInfo.soft ? 'info' : 'alert-triangle'} size={16} />
              <span style={{ fontSize: 13 }}>
                {ucsInfo.soft ? 'Dane wysyłki z UCS niedostępne: ' : 'Nie udało się pobrać danych z UCS: '}{ucsInfo.message}
              </span>
            </div>
          )}

          {/* Rozpoznany kurier (UCS identified). */}
          {ident && (
            <>
              <SectionHead>Rozpoznany kurier (UCS)</SectionHead>
              <div className="card card-pad" style={{ padding: 14, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {identCourier && <CourierBadge courier={identCourier} lg />}
                <dl className="dl-grid" style={{ gridTemplateColumns: '110px 1fr', flex: 1, minWidth: 200 }}>
                  <dt>Plugin</dt><dd className="mono">{ident.pluginId}</dd>
                  <dt>Usługa</dt><dd className="mono">{ident.serviceId}</dd>
                  {ident.qualifier && (<><dt>Punkt / kod</dt><dd className="mono">{ident.qualifier}</dd></>)}
                </dl>
              </div>
            </>
          )}

          {/* Pozycje zamówienia z UCS (line_items) — pełna lista produktów. */}
          {lineItems.length > 0 && (
            <>
              <SectionHead>Pozycje zamówienia ({lineItems.length})</SectionHead>
              <div className="card" style={{ marginBottom: 20 }}>
                <div className="tablewrap">
                  <table className="tbl">
                    <thead><tr><th>Produkt</th><th>SKU</th><th style={{ textAlign: 'right' }}>Ilość</th><th style={{ textAlign: 'right' }}>Waga</th><th style={{ textAlign: 'right' }}>Cena</th></tr></thead>
                    <tbody>
                      {lineItems.map((it, i) => (
                        <tr key={it.id ?? i}>
                          <td>
                            <div style={{ fontWeight: 500 }}>{it.title || '—'}</div>
                            {(it.variant_id || it.product_id) && (
                              <div className="muted mono" style={{ fontSize: 11 }}>
                                {it.product_id ? `prod ${it.product_id}` : ''}{it.product_id && it.variant_id ? ' · ' : ''}{it.variant_id ? `var ${it.variant_id}` : ''}
                              </div>
                            )}
                          </td>
                          <td className="mono" style={{ fontSize: 12 }}>{it.sku || '—'}</td>
                          <td className="num" style={{ textAlign: 'right' }}>{it.quantity ?? '—'}</td>
                          <td className="mono num" style={{ textAlign: 'right', fontSize: 12 }}>{fmtGrams(it.grams) || '—'}</td>
                          <td className="mono num" style={{ textAlign: 'right' }}>{it.price ? fmtMoney(it.price, currency) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Adresy (dostawy / rozliczeniowy). */}
          {(addressLines(ucsOrder?.shipping_address) || addressLines(ucsOrder?.billing_address)) && (
            <>
              <SectionHead>Adresy</SectionHead>
              <div className="flex" style={{ gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'stretch' }}>
                <AddressCard title="Dostawa" addr={ucsOrder?.shipping_address} />
                <AddressCard title="Rozliczeniowy" addr={ucsOrder?.billing_address} />
              </div>
            </>
          )}

          {/* Linie wysyłkowe Shopify (metoda dostawy z checkoutu). */}
          {shippingLines.length > 0 && (
            <>
              <SectionHead>Linie wysyłkowe</SectionHead>
              <div className="grid" style={{ gap: 10, marginBottom: 20 }}>
                {shippingLines.map((sl, i) => (
                  <div className="card card-pad" key={sl.id ?? i} style={{ padding: 12 }}>
                    <div className="flex" style={{ justifyContent: 'space-between', gap: 10 }}>
                      <div style={{ fontWeight: 600 }}>{sl.title || '—'}</div>
                      <div className="mono num">{sl.price ? fmtMoney(sl.price, currency) : '—'}</div>
                    </div>
                    {(sl.code || sl.carrier_identifier) && (
                      <div className="muted mono" style={{ fontSize: 11, marginTop: 4 }}>
                        {sl.code || ''}{sl.code && sl.carrier_identifier ? ' · ' : ''}{sl.carrier_identifier || ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Stan fulfillmentu (co wysłane / co do spakowania) z UCS. */}
          {ful && (
            <>
              <SectionHead>Stan fulfillmentu</SectionHead>
              <div className="card card-pad" style={{ padding: 14, marginBottom: 20 }}>
                {!ful.available ? (
                  <div className="muted" style={{ fontSize: 13 }}>
                    Niedostępny{ful.reason ? ` (${ful.reason})` : ''}{ful.message ? ` — ${ful.message}` : ''}
                  </div>
                ) : (
                  <>
                    <dl className="dl-grid" style={{ gridTemplateColumns: '140px 1fr', marginBottom: ful.lineItems?.length || ful.existingFulfillments?.length ? 12 : 0 }}>
                      <dt>Status</dt><dd className="mono">{ful.fulfillmentStatus || '—'}</dd>
                    </dl>
                    {!!ful.lineItems?.length && (
                      <>
                        <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', margin: '4px 0 8px' }}>Do wysyłki</div>
                        {ful.lineItems.map((li, i) => {
                          const ord = lineItems.find((x) => x.id === li.orderLineItemId);
                          return (
                            <div key={li.fulfillmentOrderLineItemId} className="flex" style={{ justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderTop: i ? '1px solid var(--divider)' : 0 }}>
                              <span>{ord?.title || li.sku || `Pozycja ${li.orderLineItemId}`}</span>
                              <span className="mono num">{li.remainingQuantity} / {li.totalQuantity}</span>
                            </div>
                          );
                        })}
                      </>
                    )}
                    {!!ful.existingFulfillments?.length && (
                      <>
                        <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', margin: '12px 0 8px' }}>Wysłane</div>
                        {ful.existingFulfillments.map((f) => (
                          <div key={f.id} style={{ fontSize: 13, padding: '4px 0' }}>
                            <div className="flex" style={{ justifyContent: 'space-between', gap: 10 }}>
                              <span className="mono">{f.status}{f.trackingCompany ? ` · ${f.trackingCompany}` : ''}</span>
                              <span className="muted mono" style={{ fontSize: 12 }}>{new Date(f.createdAt).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                            {f.trackingNumbers?.map((tn, j) => (
                              <div key={tn} className="mono" style={{ fontSize: 12, color: 'var(--accent-text)' }}>
                                {f.trackingUrls?.[j]
                                  ? <a href={f.trackingUrls[j]} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{tn}</a>
                                  : tn}
                              </div>
                            ))}
                          </div>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* Gotowy shipmentRequest z UCS (do POST /v1/shipments). */}
          {ucs?.shipmentRequest && (
            <>
              <SectionHead>Dane wysyłki (UCS)</SectionHead>
              <div className="card card-pad" style={{ padding: 14, marginBottom: 20 }}>
                <dl className="dl-grid" style={{ gridTemplateColumns: '140px 1fr' }}>
                  {ucs.shipmentRequest.reference && (<><dt>Referencja</dt><dd className="mono">{ucs.shipmentRequest.reference}</dd></>)}
                  {ucs.shipmentRequest.carrierId && (<><dt>Carrier</dt><dd className="mono">{ucs.shipmentRequest.carrierId}</dd></>)}
                  {ucs.shipmentRequest.serviceId && (<><dt>Usługa</dt><dd className="mono">{ucs.shipmentRequest.serviceId}</dd></>)}
                  {!!ucs.shipmentRequest.parcels?.length && (
                    <><dt>Paczki</dt><dd className="mono">
                      {ucs.shipmentRequest.parcels.map((p, i) => {
                        const w = p.weight?.value != null ? `${p.weight.value} ${p.weight.unit || ''}`.trim() : null;
                        return <span key={i}>{i ? ', ' : ''}{w || `#${i + 1}`}</span>;
                      })}
                    </dd></>
                  )}
                </dl>
              </div>
            </>
          )}

          {attrs.length > 0 && (
            <>
              <SectionHead>Atrybuty zamówienia</SectionHead>
              <div className="flex" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                {attrs.slice(0, 8).map((a, i) => (
                  <span className="tag-attr" key={i}>{a.key}: {String(a.value).slice(0, 40)}</span>
                ))}
              </div>
            </>
          )}

          {/* „Paczki" to stan PAKOWANIA w PackFlow — nie mieszać ze „Stanem fulfillmentu" z UCS
              powyżej, który pokazuje ilości DO WYSYŁKI. Zamówienie ze skompletowanymi pozycjami
              w UCS i zerem paczek nie jest sprzecznością: po prostu nikt go tu jeszcze nie pakował. */}
          <SectionHead>Paczki spakowane w PackFlow ({pkgs.length})</SectionHead>
          <div className="grid" style={{ gap: 10, marginBottom: 20 }}>
            {pkgs.length === 0 && (
              <div className="muted" style={{ fontSize: 13 }}>
                {history
                  ? 'Sesja pakowania jest w toku — żadna paczka nie została jeszcze zamknięta.'
                  : 'Tego zamówienia nie pakowano w PackFlow (brak sesji pakowania). Ilości w „Stanie fulfillmentu" powyżej to sztuki do wysyłki, nie spakowane.'}
              </div>
            )}
            {pkgs.map((p, i) => (
              <div className="card card-pad" key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14 }}>
                <Icon name="package" size={22} style={{ color: 'var(--accent-text)' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>Paczka {i + 1} · {p.packageType?.name || p.dimensions?.packageType || 'Standard'}</div>
                  <div className="muted mono" style={{ fontSize: 12 }}>
                    {p.dimensions ? `${p.dimensions.length}×${p.dimensions.width}×${p.dimensions.height} cm · ` : ''}
                    {p.weight ? `${p.weight} kg · ` : ''}
                    {p.trackingNumber || '—'}
                  </div>
                </div>
                {p.carrier && <CourierBadge courier={courierKeyOfPlugin(String(p.carrier)) || courier} />}
                <button className="btn btn-ghost" style={{ whiteSpace: 'nowrap' }} title="Drukuj etykietę kurierską tej paczki" onClick={() => printPackageLabel(p, i)}>
                  <Icon name="printer" size={16} /> Etykieta
                </button>
              </div>
            ))}
          </div>

          <div className="card-head" style={{ padding: '0 0 10px', border: 0 }}><h3>Druk dokumentów</h3></div>
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <button className="btn btn-ghost" style={{ justifyContent: 'flex-start' }} onClick={() => printDoc('packing_list')}>
              <Icon name="printer" size={16} /> {DOC_LABELS.packing_list}
            </button>
            <button className="btn btn-ghost" style={{ justifyContent: 'flex-start' }} onClick={() => printDoc('product_labels')}>
              <Icon name="printer" size={16} /> {DOC_LABELS.product_labels}
            </button>
            {/* Etykieta kurierska tylko dla rozpoznanej metody — dla nierozpoznanej UCS
                odrzuci żądanie (422), więc przycisk byłby zaproszeniem do błędu. Wydruki
                dokumentów (lista, etykiety produktów) zostają: są niezależne od kuriera. */}
            {!unidentified &&
              carriers.map((c) => (
                <button key={c} className="btn btn-ghost" style={{ justifyContent: 'flex-start' }} onClick={() => printShipping(c)}>
                  <Icon name="printer" size={16} /> Etykieta {courierLabelOf(c)}
                </button>
              ))}
          </div>
          {!unidentified && (
            <button className="btn btn-primary" style={{ width: '100%', marginTop: 12, justifyContent: 'center' }} onClick={printAll}>
              <Icon name="printer" size={16} /> Drukuj komplet
            </button>
          )}

          {/* Pełna, surowa odpowiedź UCS — gwarantuje dostęp do każdego pola API. */}
          {ucs && (
            <details style={{ marginTop: 20 }}>
              <summary className="muted" style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Surowa odpowiedź UCS (JSON)</summary>
              <pre className="mono" style={{ marginTop: 10, padding: 12, background: 'var(--surface-2)', borderRadius: 8, fontSize: 11, lineHeight: 1.5, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(ucs, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    </>
  );
}

const PAGE_SIZE = 25;

export default function OrdersPage() {
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [onlyDeliverable, setOnlyDeliverable] = useState(false);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<AdminOrder | null>(null);

  const { data: orders = [], isLoading, refetch } = useAdminOrders();
  const { data: statuses = {} } = useOrderStatuses(orders);
  const { data: packedToday = [] } = usePackedToday();
  // Magazyn: znacznik zapasu przy zamówieniu (gdy moduł włączony i znaczniki nie są wyłączone).
  const { data: stockConfig } = useStockConfig();
  const stockTagsOn = !!stockConfig?.enabled && !!stockConfig?.showOrderTags;
  const { data: stockOv } = useStockOverview({ enabled: stockTagsOn });
  const stockTags = useMemo(() => {
    const byId = new Map<string, OrderStockTag>();
    const byName = new Map<string, OrderStockTag>();
    // Bez wpisu od resetu znaczniki byłyby same czerwone — ukrywamy je jak w kiosku.
    if (stockTagsOn && stockOv?.enabled && stockOv.stockKnown) {
      stockOv.orders.forEach((t) => {
        byId.set(stockIdKey(t.orderId), t);
        byName.set(stockNameKey(t.orderName), t);
      });
    }
    return { byId, byName };
  }, [stockTagsOn, stockOv]);
  const stockTagOf = (o: AdminOrder): OrderStockTag | null =>
    stockTags.byId.get(stockIdKey(o.id)) || stockTags.byName.get(stockNameKey(o.name)) || null;

  const withStatus = useMemo(() => {
    // Status z kartoteki wygrywa, ale gdy go nie ma (starsze API, brak wiersza), zostaje ten
    // wyliczony z fulfillmentu UCS — inaczej zamówienie zrealizowane poza PackFlow spadałoby
    // do „Oczekuje".
    const base = orders.map((o) => ({ ...o, packingStatus: normStatus(statuses[o.id] ?? o.packingStatus) }));
    // Spakowane dziś doklejamy tylko wtedy, gdy nie ma ich już w kolejce UCS (po spakowaniu
    // zamówienie zostaje zrealizowane i z kolejki wypada — stąd „Spakowane 0" przed tą zmianą).
    const known = new Set(base.map((o) => String(o.id)));
    const extra = packedToday
      .filter((p) => p.id != null && !known.has(String(p.id)))
      .map(packedToAdminOrder);
    return [...base, ...extra];
  }, [orders, statuses, packedToday]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: withStatus.length };
    withStatus.forEach((o) => { c[o.packingStatus] = (c[o.packingStatus] || 0) + 1; });
    return c;
  }, [withStatus]);

  // Szukanie po dowolnym fragmencie: numer zamówienia, klient (imię + nazwisko)
  // oraz miasto. Normalizacja (małe litery, bez diakrytyków) — „lodz" znajdzie
  // „Łódź", „brzezno" znajdzie „BRZEŹNO".
  const nq = normalizePlace(q);
  const filtered = withStatus.filter((o) => {
    const okF = filter === 'all' || o.packingStatus === filter;
    const haystack = normalizePlace(
      `${o.name || ''} ${o.customer?.first_name || ''} ${o.customer?.last_name || ''} ${o.shipping_address?.city || ''}`,
    );
    const okQ = !nq || haystack.includes(nq);
    const okD = !onlyDeliverable || isRelevantOrder(o);
    return okF && okQ && okD;
  });

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  if (isLoading && !orders.length) return <PageSkeleton table />;

  const fdefs: [string, string][] = [['all', 'Wszystkie'], ['pending', 'Oczekuje'], ['inprogress', 'W trakcie'], ['packed', 'Spakowane'], ['error', 'Błąd']];

  return (
    <div className="page">
      <div className="page-head">
        <div><h1>Zamówienia</h1><div className="ph-sub">Kolejka z UCS · auto-odświeżanie co 30 s</div></div>
        <div className="actions">
          <div className="search"><Icon name="search" size={16} /><input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Szukaj #numer / klient / miasto…" /></div>
          <button className="btn btn-ghost" onClick={() => refetch()}><Icon name="refresh-cw" size={16} /></button>
        </div>
      </div>

      <div className="flex" style={{ justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div className="chips">
          {fdefs.map(([id, lbl]) => (
            <button key={id} className="chip" aria-pressed={filter === id} onClick={() => { setFilter(id); setPage(0); }}>{lbl}<span className="c">{counts[id] || 0}</span></button>
          ))}
        </div>
        <button className="chip" aria-pressed={onlyDeliverable} onClick={() => { setOnlyDeliverable((v) => !v); setPage(0); }}><Icon name="calendar-check" size={14} /> Data dostawy ≤ jutro</button>
      </div>

      <div className="card">
        <div className="tablewrap">
          <table className="tbl">
            <thead><tr><th>#</th><th>Klient</th><th>Miasto</th><th>Data dostawy</th><th>Poz.</th><th>Wartość</th><th>Kurier</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {pageRows.map((o) => (
                <tr className="row" key={o.id} onClick={() => setSelected(o)}>
                  <td className="mono" style={{ fontWeight: 600 }}>{o.name}</td>
                  <td>{o.customer?.first_name} {o.customer?.last_name}</td>
                  <td className="muted">{o.shipping_address?.city || '—'}</td>
                  <td className="mono num" style={{ fontSize: 13 }}>{formatDeliverySlot(o)}</td>
                  <td className="num">{o.line_items?.reduce((s, i) => s + i.quantity, 0) ?? '—'}</td>
                  <td className="num">{fmtMoney(o.total_price, o.currency)}</td>
                  <td>
                    {/* Nierozpoznana metoda wysyłki bije heurystykę: dotąd spadała na
                        zahardkodowany 'inpost' i cała lista świeciła żółtym INPOST-em. */}
                    <CourierBadge courier={isUnidentifiedOrder(o) ? 'unknown' : courierOfOrder(o)} />
                  </td>
                  <td>
                    <div className="mga-statuscell">
                      <StatusPill status={o.packingStatus} />
                      {o.packingStatus !== 'packed' && (() => { const t = stockTagOf(o); return t ? <StockTagPill tag={t} /> : null; })()}
                    </div>
                  </td>
                  <td><Icon name="chevron-right" size={16} style={{ color: 'var(--fg-3)' }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="empty"><div className="ei"><Icon name="search-x" size={28} /></div><h3>Brak wyników</h3><p>Zmień filtry lub frazę wyszukiwania.</p></div>
        )}
      </div>
      <div className="flex" style={{ justifyContent: 'space-between', marginTop: 14, color: 'var(--fg-3)', fontSize: 13 }}>
        <span>{filtered.length} z {withStatus.length} zamówień</span>
        <div className="flex">
          <button className="btn btn-ghost btn-sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>‹ Poprzednia</button>
          <span className="mono">{page + 1} / {pages}</span>
          <button className="btn btn-ghost btn-sm" disabled={page >= pages - 1} onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}>Następna ›</button>
        </div>
      </div>

      {selected && (
        <OrderDrawer
          order={selected}
          status={selected.packingStatus || 'pending'}
          onClose={() => setSelected(null)}
          stockTag={selected.packingStatus !== 'packed' ? stockTagOf(selected) : null}
        />
      )}
    </div>
  );
}
